import { createContext, useContext, useCallback, useEffect, useState, useMemo, useRef, type ReactNode } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { promoteFile } from "@/network/network";
import {
  createPromoteMarketingAssetSchema,
  type MarketingAssetFormData,
} from "@/react/schema/marketingAssetSchema";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { MarketingAsset, RESTRICTION_REASON_OPTIONS } from "@/bryntum-lib/models/MarketingAsset";
import { type ProblemSolutionTagMap } from "@/bryntum-lib/models/ProblemSolutionTagMap";
import { type TComboOption } from "@/modern/components/ui/combobox";

export const PROMOTE_STEPS = [
  {
    label: "Classify",
    helpText: "When this photo was taken relative to the work phase (before, during, or after).",
  },
  {
    label: "Describe",
    helpText:
      "Pick at least one solution tag. Caption and alt text are optional but help this asset in marketing, search, and accessibility.",
  },
  {
    label: "Role",
    helpText: "What role does this image play? Assign content roles so each asset is grouped into the right section on the generated page.",
  },
  {
    label: "Promote",
    helpText: "Visibility and restrictions before this asset is approved for use.",
  },
] as const;

export const TEMPORAL_DESCRIPTIONS: Record<string, string> = {
  Before: "Pre-construction / inspection state",
  During: "Active work in progress",
  After: "Post-completion / finished state",
};

interface PromotePanelContextValue {
  currentStep: number;
  goNext: () => void;
  goBack: () => void;
  goToStep: (index: number) => void;
  canNavigateToStep: (index: number) => boolean;
  canProceed: boolean;
  direction: number;

  form: UseFormReturn<MarketingAssetFormData>;
  loading: boolean;
  submitting: boolean;
  promoted: boolean;

  aiSuggestions: string[];
  allTags: MarketingTag[];
  temporalTags: MarketingTag[];
  folderTags: MarketingTag[];
  nonFolderNonTemporalTags: MarketingTag[];

  toggleTag: (tagId: number) => void;
  onSubmit: () => void;

  restrictionReasonOptions: TComboOption[];

  addedFolders: MarketingTag[];
  availableFolders: MarketingTag[];
  suggestedFolderIds: number[];
  suggestedSolutionTagIds: number[];

  linkedProblemTags: MarketingTag[];

  isEditing: boolean;
  demoting: boolean;
  onDemote: () => void;
}

const PromotePanelContext = createContext<PromotePanelContextValue | undefined>(undefined);

export const PromotePanelProvider = ({ children }: { children: ReactNode }) => {
  const { file, isFilePromoted, project, setPromoteAiSuggestions, setPromoteVisibilityWarnings, handleClosePromotePanel } = useFileDetailModal();
  const { user, selectedBoard } = useSwivuBryntumContext();
  const { version: marketingTagStoreVersion } = useSwivuStore<MarketingTag>({
    storeId: STORE_KEYS.marketingTagStore,
  });

  // Lock mode for this panel session so local store updates during promote
  // don't switch UI into "edit" state mid-submit.
  const isEditingRef = useRef(isFilePromoted);
  const isEditing = isEditingRef.current;

  const existingAsset = useMemo<MarketingAsset | null>(() => {
    if (!isEditing || !project || !file) return null;
    const records = project.marketingAssetStore?.query(
      (r: MarketingAsset) => r.file === file.id,
      true,
    ) as MarketingAsset[] | undefined;
    return records?.[0] ?? null;
  }, [isEditing, project, file]);

  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [promoted, setPromoted] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [suggestedFolderIds, setSuggestedFolderIds] = useState<number[]>([]);
  const [suggestedSolutionTagIds, setSuggestedSolutionTagIds] = useState<number[]>([]);

  const allTags: MarketingTag[] = (project?.marketingTagStore?.query(() => true, true) as MarketingTag[]) ?? [];
  const temporalTags = allTags.filter((t) => t.type === MarketingTagType.TEMPORAL);
  const folderTags = allTags.filter((t) => t.type === MarketingTagType.CONTENT || t.isFolderEligible);
  const nonFolderNonTemporalTags = allTags.filter((t) => t.type === MarketingTagType.SOLUTION || t.type === MarketingTagType.PROBLEM);

  const restrictionReasonOptions: TComboOption[] = RESTRICTION_REASON_OPTIONS.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));

  const solutionTagIdSet = useMemo(() => {
    const tags = (project?.marketingTagStore?.query(() => true, true) as MarketingTag[]) ?? [];
    return new Set(
      tags
        .filter((t) => t.type === MarketingTagType.SOLUTION)
        .map((t) => t.id),
    );
  }, [project, marketingTagStoreVersion]);

  const promoteSchema = useMemo(
    () => createPromoteMarketingAssetSchema(solutionTagIdSet),
    [solutionTagIdSet],
  );

  const form = useForm<MarketingAssetFormData>({
    resolver: zodResolver(promoteSchema),
    defaultValues: {
      caption: "",
      altText: "",
      aiObservations: [],
      visibilityWarnings: [],
      tags: [],
      temporalTagId: undefined,
      isRestricted: false,
      restrictionReason: null,
    },
  });

  const { setValue, watch, reset, handleSubmit } = form;
  const watchTags = watch("tags");
  const watchTemporalTagId = watch("temporalTagId");

  const addedFolders = folderTags.filter((t) => watchTags?.includes(t.id));
  const availableFolders = folderTags.filter((t) => !watchTags?.includes(t.id));

  const linkedProblemTags = useMemo(() => {
    if (!project?.problemSolutionTagMapStore || !watchTags?.length) return [];

    const selectedSolutionIds = new Set(
      watchTags.filter((id) => solutionTagIdSet.has(id)),
    );
    if (selectedSolutionIds.size === 0) return [];

    const maps = project.problemSolutionTagMapStore.query(
      (m: ProblemSolutionTagMap) => selectedSolutionIds.has(m.solutionTag),
      true,
    ) as ProblemSolutionTagMap[];

    const problemTagIds = new Set(maps.map((m) => m.problemTag));

    return allTags.filter(
      (t) => t.type === MarketingTagType.PROBLEM && problemTagIds.has(t.id),
    );
  }, [project, watchTags, solutionTagIdSet, allTags]);

  const hasRequiredSolutionTag = useMemo(() => {
    return watchTags?.some((id) => solutionTagIdSet.has(id)) ?? false;
  }, [solutionTagIdSet, watchTags]);

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 0:
        return watchTemporalTagId != null;
      case 1:
        return hasRequiredSolutionTag;
      case 2:
      case 3:
        return true;
      default:
        return false;
    }
  }, [currentStep, watchTemporalTagId, hasRequiredSolutionTag]);

  const goNext = useCallback(() => {
    if (!canProceed) return;
    setDirection(1);
    setCurrentStep((s) => Math.min(s + 1, PROMOTE_STEPS.length - 1));
  }, [canProceed]);

  const goBack = useCallback(() => {
    setDirection(-1);
    setCurrentStep((s) => Math.max(s - 1, 0));
  }, []);

  const canNavigateToStep = useCallback(
    (targetIndex: number) => {
      if (submitting) return false;
      if (targetIndex === currentStep) return false;
      if (targetIndex < 0 || targetIndex >= PROMOTE_STEPS.length) return false;
      if (targetIndex < currentStep) return true;
      if (watchTemporalTagId == null) return false;
      if (targetIndex >= 2) {
        const hasSolution = watchTags?.some((id) => solutionTagIdSet.has(id)) ?? false;
        if (!hasSolution) return false;
      }
      return true;
    },
    [submitting, currentStep, watchTemporalTagId, watchTags, solutionTagIdSet],
  );

  const goToStep = useCallback(
    (targetIndex: number) => {
      if (!canNavigateToStep(targetIndex)) return;
      setDirection(targetIndex > currentStep ? 1 : -1);
      setCurrentStep(targetIndex);
    },
    [canNavigateToStep, currentStep],
  );

  const fetchPromoteData = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      const response = await promoteFile(file.id);
      const data = response.data;

      const tagIds: number[] = data.suggested_tag_ids || [];

      const suggestedTags = tagIds.filter((id) => {
        const tag = allTags.find((t) => t.id === id);
        return tag && tag.type !== MarketingTagType.TEMPORAL;
      });

      const suggestedTemporal = tagIds.find((id) => {
        const tag = allTags.find((t) => t.id === id);
        return tag?.type === MarketingTagType.TEMPORAL;
      });

      const folderSuggestions = suggestedTags.filter((id) => {
        const tag = allTags.find((t) => t.id === id);
        return tag?.isFolderEligible;
      });
      setSuggestedFolderIds(folderSuggestions);

      const solutionSuggestions = suggestedTags.filter((id) => {
        const tag = allTags.find((t) => t.id === id);
        return tag?.type === MarketingTagType.SOLUTION;
      });
      setSuggestedSolutionTagIds(solutionSuggestions);

      reset({
        caption: data.caption_draft || "",
        altText: data.alt_text_draft || "",
        aiObservations: data.ai_observations || [],
        visibilityWarnings: data.visibility_warnings || [],
        tags: suggestedTags,
        temporalTagId: suggestedTemporal ?? temporalTags[0]?.id,
        isRestricted: data.is_restricted ?? false,
        restrictionReason: data.restriction_reason ?? null,
      });

      const observations = data.ai_observations || [];
      setAiSuggestions(observations);
      setPromoteAiSuggestions(observations);
      setPromoteVisibilityWarnings(data.visibility_warnings || []);
    } catch {
      toast.error("Failed to analyze file for promotion.");
    } finally {
      setLoading(false);
    }
  }, [file, allTags, temporalTags, reset, setPromoteAiSuggestions, setPromoteVisibilityWarnings]);

  useEffect(() => {
    if (isEditing && existingAsset) {
      const existingTemporal = existingAsset.tags?.find((id: number) =>
        temporalTags.some((t) => t.id === id),
      );
      const existingNonTemporal = existingAsset.tags?.filter(
        (id: number) => !temporalTags.some((t) => t.id === id),
      ) ?? [];

      reset({
        caption: existingAsset.caption ?? "",
        altText: existingAsset.altText ?? "",
        aiObservations: existingAsset.aiObservations ?? [],
        visibilityWarnings: existingAsset.visibilityWarnings ?? [],
        tags: existingNonTemporal,
        temporalTagId: existingTemporal ?? temporalTags[0]?.id,
        isRestricted: existingAsset.isRestricted ?? false,
        restrictionReason: existingAsset.restrictionReason ?? null,
      });

      setAiSuggestions(existingAsset.aiObservations ?? []);
      setPromoteAiSuggestions(existingAsset.aiObservations ?? []);
      setPromoteVisibilityWarnings(existingAsset.visibilityWarnings ?? []);
    } else if (!isEditing) {
      fetchPromoteData();
    }
  }, [file?.id]);

  const toggleTag = useCallback(
    (tagId: number) => {
      const current = watchTags || [];
      if (current.includes(tagId)) {
        setValue("tags", current.filter((id) => id !== tagId), { shouldValidate: true });
      } else {
        setValue("tags", [...current, tagId], { shouldValidate: true });
      }
    },
    [watchTags, setValue],
  );

  const handleFormSubmit = useCallback(
    async (data: MarketingAssetFormData) => {
      if (!project || !file) return;
      setSubmitting(true);

      try {
        const linkedProblemTagIds = linkedProblemTags.map((t) => t.id);
        const allSelectedTags = [...data.tags, data.temporalTagId, ...linkedProblemTagIds];

        if (isEditing && existingAsset) {
          existingAsset.set({
            caption: data.caption,
            altText: data.altText,
            aiObservations: data.aiObservations,
            visibilityWarnings: data.visibilityWarnings,
            isRestricted: data.isRestricted,
            restrictionReason: data.restrictionReason,
            tags: allSelectedTags,
          });
        } else {
          project.marketingAssetStore.add({
            board: selectedBoard?.id,
            file: file.id,
            caption: data.caption,
            alt_text: data.altText,
            ai_observations: data.aiObservations,
            visibility_warnings: data.visibilityWarnings,
            is_restricted: data.isRestricted,
            restriction_reason: data.restrictionReason,
            tags: allSelectedTags,
          });
        }

        await project.sync();

        setSubmitting(false);
        setPromoted(true);
      } catch {
        setSubmitting(false);
        toast.error(isEditing ? "Failed to save changes." : "Failed to promote file.");
      }
    },
    [project, file, user, isEditing, existingAsset, linkedProblemTags],
  );

  const onSubmit = useCallback(() => {
    handleSubmit(handleFormSubmit)();
  }, [handleSubmit, handleFormSubmit]);

  const [demoting, setDemoting] = useState(false);

  const onDemote = useCallback(async () => {
    if (!project || !existingAsset) return;
    setDemoting(true);
    try {
      project.marketingAssetStore.remove(existingAsset.id);
      handleClosePromotePanel();
    } catch {
      toast.error("Failed to demote asset.");
    } finally {
      setDemoting(false);
    }
  }, [project, existingAsset, handleClosePromotePanel]);

  const value: PromotePanelContextValue = {
    currentStep,
    goNext,
    goBack,
    goToStep,
    canNavigateToStep,
    canProceed,
    direction,
    form,
    loading,
    submitting,
    promoted,
    aiSuggestions,
    allTags,
    temporalTags,
    folderTags,
    nonFolderNonTemporalTags,
    toggleTag,
    onSubmit,
    restrictionReasonOptions,
    addedFolders,
    availableFolders,
    suggestedFolderIds,
    suggestedSolutionTagIds,
    linkedProblemTags,
    isEditing,
    demoting,
    onDemote,
  };

  return <PromotePanelContext.Provider value={value}>{children}</PromotePanelContext.Provider>;
};

export const usePromotePanel = () => {
  const context = useContext(PromotePanelContext);
  if (!context) {
    throw new Error("usePromotePanel must be used within a PromotePanelProvider");
  }
  return context;
};
